<template>
  <transition name="el-fade-in-linear">
    <div v-show="dialogVisible" class="popContainer indiv-pop">
      <div class="indiv-pop-center">
        <span
          class="indiv-pop-close iconfont icon-close_round_fill_bold"
          role="button"
          aria-label="关闭"
          @click="onClose"
        ></span>
        <div class="indiv-pop-box buy-pay-pop">
          <div class="indiv-pop-title">扫码支付</div>
          <div class="buy-pay-body">
            <p class="pay-price">
              <span style="font-size: 24px">¥</span> {{ displayAmount }}
            </p>
            <p v-if="showTrialTip" class="pay-trial-badge">
              赠送剩余试用时间 {{ daysLeft }} 天
            </p>

            <!-- 本地测试：模拟支付 -->
            <div v-if="useSimulatePay" class="pay-test">
              <p class="pay-test-tip">本地测试模式</p>
              <button
                type="button"
                class="indiv-pop-btn"
                :disabled="paying"
                @click="onSimulatePay"
              >
                {{ paying ? "处理中..." : "模拟支付" }}
              </button>
              <p v-if="loadError" class="pay-qr-error">{{ loadError }}</p>
            </div>

            <!-- 正式环境：支付宝扫码 -->
            <template v-else>
              <div
                v-loading="loading"
                class="pay-qr"
                element-loading-text="加载支付码..."
              >
                <iframe
                  v-if="!loadError"
                  :key="qrLoadSeq"
                  ref="qrFrame"
                  class="pay-qr-frame"
                  title="扫码支付"
                  frameborder="0"
                  scrolling="no"
                ></iframe>
                <p v-if="loadError" class="pay-qr-error">{{ loadError }}</p>
              </div>
              <p class="pay-channel">
                使用
                <span class="pay-channel-item">
                  <img
                    class="pay-icon"
                    :src="alipayPayIcon"
                    alt=""
                  />支付宝</span
                >扫码支付
              </p>
              <p v-if="polling" class="pay-wait">等待支付中...</p>
            </template>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  USE_SIMULATE_PAY,
  PAY_CHANNEL,
  QR_MODE,
  QR_WIDTH,
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
} from "./config";
import { fetchPayParams, fetchOrderQuery, simulatePay } from "./api";
import { subscriptionState } from "../indivSession/indivSessionService";
import alipayPayIcon from "@/view/user/purchaseService/img/zfb.png";

export default {
  name: "PayQrDialog",

  props: {
    visible: { type: Boolean, default: false },
    orderNo: { type: String, default: "" },
    amount: { type: [String, Number], default: "" },
    showTrialTip: { type: Boolean, default: false },
  },

  data() {
    return {
      alipayPayIcon,
      useSimulatePay: USE_SIMULATE_PAY,
      loading: false,
      paying: false,
      loadError: "",
      polling: false,
      pollTimer: null,
      pollTimeoutTimer: null,
      paid: false,
      // 每次 loadQr +1：重建 iframe（:key）并标记当前有效请求
      qrLoadSeq: 0,
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },

    displayAmount() {
      const num = Number(this.amount);
      if (Number.isNaN(num)) {
        return this.amount || "0.00";
      }
      return num.toLocaleString("zh-CN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    daysLeft() {
      const active = subscriptionState.activeData;
      if (active && active.daysLeft != null) {
        return active.daysLeft;
      }
      const status = subscriptionState.status;
      if (status && status.daysLeft != null) {
        return status.daysLeft;
      }
      return 0;
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.startPay();
      } else {
        this.stopPay();
      }
    },

    orderNo(val, oldVal) {
      if (this.visible && val && val !== oldVal) {
        this.startPay();
      }
    },
  },

  beforeDestroy() {
    this.stopPay();
  },

  methods: {
    startPay() {
      if (!this.orderNo) {
        return;
      }
      this.paid = false;
      this.loadError = "";
      if (this.useSimulatePay) {
        return;
      }
      this.loadQr();
      this.startPoll();
    },

    stopPay() {
      this.qrLoadSeq += 1; // 作废尚未返回的 pay_params
      this.stopPoll();
      this.loading = false;
      this.paying = false;
      this.polling = false;
    },

    loadQr() {
      const seq = ++this.qrLoadSeq;
      const orderNo = this.orderNo;
      this.loading = true;
      this.loadError = "";

      fetchPayParams({
        orderNo,
        channel: PAY_CHANNEL,
        qrmode: QR_MODE,
        qrwidth: QR_WIDTH,
      })
        .then((data) => {
          if (!this.isActiveLoad(seq, orderNo)) return;
          this.renderQr(data && data.payParams, seq, orderNo);
        })
        .catch((err) => {
          if (!this.isActiveLoad(seq, orderNo)) return;
          this.loadError = (err && err.message) || "支付码加载失败";
        })
        .finally(() => {
          if (this.isActiveLoad(seq, orderNo)) {
            this.loading = false;
          }
        });
    },

    /** 弹窗仍开着，且序号、订单号与发起请求时一致 */
    isActiveLoad(seq, orderNo) {
      return (
        this.visible && seq === this.qrLoadSeq && orderNo === this.orderNo
      );
    },

    renderQr(html, seq, orderNo) {
      if (!html) {
        this.loadError = "支付参数为空";
        return;
      }

      this.$nextTick(() => {
        if (!this.isActiveLoad(seq, orderNo)) return;
        const frame = this.$refs.qrFrame;
        if (!frame) return;
        const doc = frame.contentDocument || frame.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
      });
    },

    onSimulatePay() {
      if (!this.orderNo || this.paying || this.paid) {
        return;
      }
      this.paying = true;
      this.loadError = "";

      simulatePay(this.orderNo)
        .then(() => fetchOrderQuery(this.orderNo))
        .then((data) => {
          if (data && Number(data.payStatus) === 1) {
            this.onPaid(data);
            return;
          }
          throw new Error("支付状态未更新");
        })
        .catch((err) => {
          this.loadError = (err && err.message) || "模拟支付失败";
        })
        .finally(() => {
          this.paying = false;
        });
    },

    startPoll() {
      this.stopPoll();
      this.polling = true;
      this.pollTimer = setInterval(this.checkPayStatus, POLL_INTERVAL_MS);
      this.pollTimeoutTimer = setTimeout(this.onPollTimeout, POLL_TIMEOUT_MS);
      this.checkPayStatus();
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.pollTimeoutTimer) {
        clearTimeout(this.pollTimeoutTimer);
        this.pollTimeoutTimer = null;
      }
    },

    onPollTimeout() {
      if (this.paid || !this.dialogVisible) {
        return;
      }
      this.stopPoll();
      this.polling = false;
      this.dialogVisible = false;
      this.$message.warning("支付超时，二维码已失效，请重新发起支付");
      this.$emit("timeout");
    },

    checkPayStatus() {
      if (!this.orderNo || this.paid) {
        return;
      }

      fetchOrderQuery(this.orderNo)
        .then((data) => {
          if (data && Number(data.payStatus) === 1) {
            this.onPaid(data);
          }
        })
        .catch(() => {});
    },

    onPaid(data) {
      if (this.paid) {
        return;
      }
      this.paid = true;
      this.polling = false;
      this.stopPoll();
      this.dialogVisible = false;
      this.$emit("paid", data);
    },

    onClose() {
      this.dialogVisible = false;
    },
  },
};
</script>

<style scoped>
.indiv-pop-title {
  border-bottom: none;
  font-weight: bold;
  font-size: 16px;
  color: #000000;
}
.buy-pay-pop {
  /* width: 420px; */
}

.buy-pay-body {
  padding: 20px 100px 32px;
  text-align: center;
}

.pay-price {
  margin: 0;
  font-size: 48px;
  font-weight: 700;
  color: #e8432a;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.pay-trial-badge {
  display: inline-block;
  margin: 10px 0 0;
  padding: 4px 10px;
  font-size: 12px;
  color: #e8432a;
  line-height: 1.5;
  background: #fff0f0;
  border-radius: 4px;
}

.pay-qr {
  position: relative;
  width: 260px;
  height: 260px;
  margin: 24px auto 0;
  overflow: hidden;
}

.pay-qr-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.pay-qr-error {
  margin: 0;
  padding: 40px 16px;
  font-size: 14px;
  color: #e8432a;
  line-height: 1.6;
}

.pay-channel {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin: 20px 0 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.pay-channel-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pay-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.pay-wait {
  margin: 8px 0 0;
  font-size: 12px;
  color: #5b85ff;
}

.pay-test {
  margin-top: 20px;
  padding: 24px 16px;
  text-align: center;
  background: #fafafa;
  border: 1px dashed #ddd;
  border-radius: 4px;
}

.pay-test-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: #999;
}
</style>



// WEBPACK FOOTER //
// src/view/user/purchaseService/PayQrDialog.vue